import axios from "axios";
import Cookies from "js-cookie";
import { TOKEN_COOKIE_NAME, logout } from "../store/authSlice";
import store from "../store/store";
import { setCurrentUser } from "../utils/auth";

const isDev = process.env.NODE_ENV !== "production";
const debugLog = isDev ? console.debug.bind(console) : () => {};

debugLog(
  "[TC:boot] apiClient — REACT_APP_API_URL:",
  process.env.REACT_APP_API_URL || "(not set — falling back to relative URLs)",
);

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Attach the JWT from the cookie to every outgoing request.
// Also attach X-Tenant-Id when a tenant is set via setActiveTenant().
let _activeTenantId = null;
let _impersonationToken = null;

export function setActiveTenant(tenantId) {
  _activeTenantId = tenantId || null;
}

export function getActiveTenant() {
  return _activeTenantId;
}

export function setImpersonationToken(token) {
  _impersonationToken = token || null;
}

export function clearImpersonationToken() {
  _impersonationToken = null;
}

apiClient.interceptors.request.use((config) => {
  const path = new URL(config.url, config.baseURL || window.location.origin).pathname;
  const usesOriginalSession = path === "/api/v1/auth/keepalive" || path === "/api/v1/auth/logout";
  const originalToken = Cookies.get(TOKEN_COOKIE_NAME);
  const token = usesOriginalSession ? originalToken : _impersonationToken || originalToken;
  config.headers = config.headers || {};
  if (usesOriginalSession) {
    // A retry/config may already carry the delegated bearer. Cookie-only sessions
    // must leave Authorization absent so the backend sees the original httpOnly cookie.
    Object.keys(config.headers).forEach((name) => {
      if (["authorization", "x-tenant-id"].includes(name.toLowerCase())) delete config.headers[name];
    });
  }
  if (path === "/api/v1/auth/logout") {
    // Capture the original credentials above before dropping the in-memory delegation.
    clearImpersonationToken();
    _activeTenantId = null;
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (_activeTenantId && !usesOriginalSession) {
    config.headers["X-Tenant-Id"] = _activeTenantId;
  }
  debugLog(
    "[TC:api] -->",
    config.method?.toUpperCase(),
    config.baseURL + config.url,
    "tenant:",
    _activeTenantId,
    "hasToken:",
    !!token,
    "impersonating:",
    !!_impersonationToken,
  );
  return config;
});

// On 401 responses, clear the cookie and redirect to login.
// 403 (feature-gated, role-denied) is NOT a logout — let callers handle it.
apiClient.interceptors.response.use(
  (response) => {
    debugLog(
      "[TC:api] <--",
      response.status,
      response.config.method?.toUpperCase(),
      response.config.url,
    );
    return response;
  },
  (error) => {
    console.error(
      "[TC:api] ERR",
      error?.response?.status,
      error?.config?.method?.toUpperCase(),
      error?.config?.url,
      error?.message,
    );
    const status = error.response?.status;
    if (status === 401) {
      clearImpersonationToken();
      _activeTenantId = null;
      setCurrentUser(null);
      store.dispatch(logout());
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;

// Helper to make a request with a specific tenant header (overrides the global default)
function withTenant(tenantId) {
  return tenantId ? { headers: { "X-Tenant-Id": tenantId } } : {};
}

function mergeConfig(tenantId, extra) {
  const cfg = withTenant(tenantId);
  if (extra) {
    return { ...cfg, ...extra, headers: { ...cfg.headers, ...extra.headers } };
  }
  return cfg;
}

// ── Tenant config endpoints (tenant required via header) ─────────────────────

export function getTenantConfig(tenantId) {
  return apiClient
    .get(`/api/v1/tenant/config`, withTenant(tenantId))
    .then((r) => r.data);
}

export function updateTenantConfig(tenantId, changes) {
  return apiClient
    .put(`/api/v1/tenant/config`, changes, withTenant(tenantId))
    .then((r) => r.data);
}

export function sendTestEmailTemplate(tenantId, templateId, toEmail) {
  return apiClient
    .post(
      `/api/v1/tenant/email-templates/test`,
      { templateId, toEmail },
      withTenant(tenantId),
    )
    .then((r) => r.data);
}

export function getEmailTemplateRegistry(tenantId) {
  return apiClient
    .get(`/api/v1/tenant/email-templates/registry`, withTenant(tenantId))
    .then((r) => r.data);
}

/**
 * Resolve a tenant by the current browser hostname.
 * Returns { id, name, logo, theme, features } or null if no match.
 */
export function resolveTenantByDomain(hostname) {
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  return fetch(
    `${base}/api/v1/admin/tenants/by-domain?hostname=${encodeURIComponent(hostname)}`,
  )
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
}

/**
 * Fetch all tenants (public endpoint, no authentication required).
 * Returns basic tenant info: id, name, logo, icon
 */
export function fetchAllTenants() {
  return apiClient
    .get(`/api/v1/admin/tenants/public`)
    .then((r) => r.data)
    .catch(() => []);
}

/**
 * Ping the public auth health endpoint to determine if the backend is reachable.
 * Never rejects — resolves to { up, service, checkedAt }.
 */
export function fetchSystemHealth() {
  const checkedAt = () => new Date().toISOString();
  return apiClient
    .get(`/api/v1/auth/health`)
    .then((r) => ({
      up: r.data?.status === "UP",
      service: r.data?.service || "authentication",
      checkedAt: checkedAt(),
    }))
    .catch(() => ({
      up: false,
      service: "authentication",
      checkedAt: checkedAt(),
    }));
}

// ── User endpoints (no tenant in path or header) ─────────────────────────────

function unwrapDataOrArray(response) {
  const data = response.data;
  // Some endpoints return { data: [...], total: X } while others return a raw array.
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

export function fetchUsers() {
  return apiClient.get(`/api/v1/users`).then(unwrapDataOrArray);
}

export function fetchUsersByIds(ids) {
  const q = ids.join(",");
  return apiClient
    .get(`/api/v1/users`, { params: { ids: q } })
    .then(unwrapDataOrArray);
}

export function createUser(user) {
  return apiClient.post(`/api/v1/users`, user).then((r) => r.data);
}

export function updateUser(userId, updates) {
  return apiClient
    .patch(`/api/v1/users/${userId}`, updates)
    .then((r) => r.data);
}

export function fetchUserProfile(userId) {
  return apiClient.get(`/api/v1/users/${userId}/profile`).then((r) => r.data);
}

export function updateUserProfile(userId, profile) {
  return apiClient
    .put(`/api/v1/users/${userId}/profile`, profile)
    .then((r) => r.data);
}

/** Returns { categories: [{ key, label, description, audience }], preferences: { [key]: { email, sms } } } for the user's roles. */
export function fetchNotificationPreferences(userId) {
  return apiClient.get(`/api/v1/users/${userId}/notification-preferences`).then((r) => r.data);
}

export function updateNotificationPreferences(userId, preferences) {
  return apiClient
    .put(`/api/v1/users/${userId}/notification-preferences`, { preferences })
    .then((r) => r.data);
}

// ── Class endpoints (tenant optional via header) ─────────────────────────────

export function fetchClassTemplates(tenantId, q, category) {
  const params = {};
  if (q) params.q = q;
  if (category) params.category = category;
  return apiClient
    .get(`/api/v1/bank/class-templates`, mergeConfig(tenantId, { params }))
    .then((r) => r.data);
}

export function fetchClassTemplateById(id) {
  return apiClient.get(`/api/v1/bank/class-templates/${id}`).then((r) => r.data);
}

export function fetchClasses(tenantId, filter, upcomingOnly) {
  const params = { q: filter || undefined };
  if (typeof upcomingOnly === "boolean") {
    params.upcomingOnly = upcomingOnly;
  }
  return apiClient
    .get(`/api/v1/classes`, mergeConfig(tenantId, { params }))
    .then((r) => r.data);
}

/**
 * Fetch publicly-listed classes (no authentication required).
 * @param {string|null} tenantId - optional tenant ID to filter classes to a single tenant
 * @param {string|null} filter   - optional search query
 */
export function fetchPublicClasses(tenantId, filter) {
  const params = { publicOnly: true, q: filter || undefined };
  const headers = tenantId ? { "X-Tenant-Id": tenantId } : {};
  return apiClient
    .get(`/api/v1/classes`, { params, headers })
    .then((r) => r.data);
}

export function fetchPublicCenterInfo(tenantId) {
  return apiClient
    .get(`/api/public/tenants/${tenantId}/center-info`)
    .then((r) => r.data);
}

export function createClass(tenantIdOrCls, cls) {
  if (tenantIdOrCls !== null && typeof tenantIdOrCls === "object") {
    const payload = tenantIdOrCls;
    return apiClient
      .post(`/api/v1/classes`, payload, withTenant(payload.tenantId))
      .then((r) => r.data);
  }

  return apiClient
    .post(`/api/v1/classes`, cls, withTenant(tenantIdOrCls))
    .then((r) => r.data);
}

export function fetchClassById(classId) {
  return apiClient.get(`/api/v1/classes/${classId}`).then((r) => r.data);
}

export function updateClass(tenantIdOrCls, classIdOrUpdates, updates) {
  if (tenantIdOrCls !== null && typeof tenantIdOrCls === "object") {
    const payload = tenantIdOrCls;
    const { id, tenantId, ...rest } = payload;
    return apiClient
      .patch(`/api/v1/classes/${id}`, rest, withTenant(tenantId))
      .then((r) => r.data);
  }

  return apiClient
    .patch(
      `/api/v1/classes/${classIdOrUpdates}`,
      updates,
      withTenant(tenantIdOrCls),
    )
    .then((r) => r.data);
}

export function deleteClass(tenantIdOrClassId, classId) {
  if (typeof classId === "undefined") {
    return apiClient
      .delete(`/api/v1/classes/${tenantIdOrClassId}`)
      .then((r) => r.data);
  }

  return apiClient
    .delete(`/api/v1/classes/${classId}`, withTenant(tenantIdOrClassId))
    .then((r) => r.data);
}

export function createClassTemplateFromClass(tenantId, classId) {
  return apiClient
    .post(`/api/v1/bank/class-templates/from-class/${classId}`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function updateClassTemplate(tenantId, id, data) {
  return apiClient
    .patch(`/api/v1/bank/class-templates/${id}`, data, withTenant(tenantId))
    .then((r) => r.data);
}

export function publishClassTemplate(tenantId, id) {
  return apiClient
    .post(`/api/v1/bank/class-templates/${id}/publish`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

/** Back to Draft (and off the marketplace if it was listed). */
export function unpublishClassTemplate(tenantId, id) {
  return apiClient
    .post(`/api/v1/bank/class-templates/${id}/unpublish`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

/** A separate draft copy ("<title> (copy)") with its sessions and placed items. */
export function duplicateClassTemplate(tenantId, id) {
  return apiClient
    .post(`/api/v1/bank/class-templates/${id}/duplicate`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function syncClassTemplateFromClass(tenantId, templateId, classId) {
  return apiClient
    .post(`/api/v1/bank/class-templates/${templateId}/sync-from-class/${classId}`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function deleteClassTemplate(tenantId, id) {
  return apiClient
    .delete(`/api/v1/bank/class-templates/${id}`, withTenant(tenantId))
    .then((r) => r.data);
}

// ── Standard courses: build and publish them (global admins only, no training center) ───────────────────────────────────

const STANDARD = "/api/v1/standard-courses";

export const listStandardCourses = () => apiClient.get(STANDARD).then((r) => r.data);
export const createStandardCourse = (data) => apiClient.post(STANDARD, data).then((r) => r.data);
export const getStandardCourse = (id) => apiClient.get(`${STANDARD}/${id}`).then((r) => r.data);
export const getStandardCourseDraft = (id) => apiClient.get(`${STANDARD}/${id}/draft`).then((r) => r.data);
export const getStandardCourseChecklist = (id) => apiClient.get(`${STANDARD}/${id}/draft/checklist`).then((r) => r.data);
export const saveStandardCourseDetails = (id, data) => apiClient.patch(`${STANDARD}/${id}/draft/details`, data).then((r) => r.data);
export const publishStandardCourse = (id, changeNote) => apiClient.post(`${STANDARD}/${id}/publish`, { changeNote }).then((r) => r.data);
export const startStandardCourseVersion = (id) => apiClient.post(`${STANDARD}/${id}/versions`).then((r) => r.data);
export const retireStandardCourse = (id) => apiClient.post(`${STANDARD}/${id}/retire`).then((r) => r.data);

/** AHA products a standard course certifies. Status is for the AHA environment this backend is connected to. */
export const listStandardCourseAhaProducts = (id) => apiClient.get(`${STANDARD}/${id}/aha-products`).then((r) => r.data);
export const linkStandardCourseAhaProduct = (id, productKey) => apiClient.post(`${STANDARD}/${id}/aha-products`, { productKey }).then((r) => r.data);
export const unlinkStandardCourseAhaProduct = (id, productKey, confirmActive = false) => apiClient
  .delete(`${STANDARD}/${id}/aha-products/${encodeURIComponent(productKey)}`, { params: confirmActive ? { confirmActive: true } : undefined }).then((r) => r.data);
/** Product key to the course that owns it. */
export const getStandardCourseAhaProductLinks = () => apiClient.get(`${STANDARD}/aha-product-links`).then((r) => r.data);
/** Course id to { count, active, verified, environment } for the course list. */
export const getStandardCourseAhaSummaries = () => apiClient.get(`${STANDARD}/aha-summaries`).then((r) => r.data);

/** kind: sessions | skill-sheets | tests | documents */
export const addStandardCourseItem = (id, kind, data) => apiClient.post(`${STANDARD}/${id}/draft/${kind}`, data).then((r) => r.data);
export const updateStandardCourseItem = (id, kind, itemId, data) => apiClient.patch(`${STANDARD}/${id}/draft/${kind}/${itemId}`, data).then((r) => r.data);
export const deleteStandardCourseItem = (id, kind, itemId) => apiClient.delete(`${STANDARD}/${id}/draft/${kind}/${itemId}`).then((r) => r.data);
export const reorderStandardCourseItems = (id, kind, ids) => apiClient.put(`${STANDARD}/${id}/draft/${kind}/reorder`, { ids });

/** A document with an uploaded file (multipart); itemId updates an existing document. */
export function saveStandardCourseDocumentFile(id, data, file, itemId) {
  const form = new FormData();
  form.append("data", new Blob([JSON.stringify(data)], { type: "application/json" }));
  form.append("file", file);
  const url = `${STANDARD}/${id}/draft/documents${itemId ? `/${itemId}` : ""}`;
  return (itemId ? apiClient.patch(url, form) : apiClient.post(url, form)).then((r) => r.data);
}

// ── Standard courses: the Standard section of the Knowledge Bank (coordinators) ───────────────────────────────────────

/** Published standard courses the training center may see (only categories it has on). */
export function fetchStandardCourses(tenantId) {
  return apiClient.get(`/api/v1/knowledge-bank/standard-courses`, withTenant(tenantId)).then((r) => r.data);
}

/** One course with everything in its current version, for the read-only preview. */
export function fetchStandardCourse(tenantId, courseId) {
  return apiClient.get(`/api/v1/knowledge-bank/standard-courses/${courseId}`, withTenant(tenantId)).then((r) => r.data);
}

/** Creates a class in the training center from the course's current version. The title and number stay the standard's. */
export function createClassFromStandardCourse(tenantId, courseId, localLabel) {
  return apiClient
    .post(`/api/v1/knowledge-bank/standard-courses/${courseId}/use`, localLabel ? { localLabel } : {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function instantiateClassTemplate(tenantId, templateId) {
  return apiClient
    .post(`/api/v1/bank/class-templates/${templateId}/instantiate`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function fetchTemplateSessions(templateId) {
  return apiClient.get(`/api/v1/bank/class-templates/${templateId}/sessions`).then((r) => r.data);
}

export function fetchTemplateSkillSheetAssignments(templateId) {
  return apiClient.get(`/api/v1/bank/class-templates/${templateId}/skill-sheets`).then((r) => r.data);
}

export function fetchTemplateTestAssignments(templateId) {
  return apiClient.get(`/api/v1/bank/class-templates/${templateId}/tests`).then((r) => r.data);
}

export function fetchTemplateDocumentAssignments(templateId) {
  return apiClient.get(`/api/v1/bank/class-templates/${templateId}/documents`).then((r) => r.data);
}

export function fetchInstructorClasses(tenantId, instructorId) {
  return apiClient
    .get(`/api/v1/classes`, mergeConfig(tenantId, { params: { instructorId } }))
    .then((r) => r.data);
}

export function fetchInstructorCourses(instructorId, tenantId, lifecycle = "current") {
  return apiClient
    .get(`/api/v1/instructors/${instructorId}/courses`, mergeConfig(tenantId, { params: { lifecycle } }))
    .then((r) => r.data);
}

export function publishClass(classId, tenantId) {
  return apiClient
    .post(`/api/v1/classes/${classId}/publish`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function unpublishClass(classId, tenantId) {
  return apiClient
    .post(`/api/v1/classes/${classId}/unpublish`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function completeClass(classId, tenantId) {
  return apiClient
    .post(`/api/v1/classes/${classId}/complete`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function fetchInstructorStats(instructorId) {
  return apiClient
    .get(`/api/v1/instructors/${instructorId}/stats`)
    .then((r) => r.data);
}

export function fetchInstructorPendingSignOffs(instructorId) {
  return apiClient
    .get(`/api/v1/instructors/${instructorId}/pending-signoffs`)
    .then((r) => r.data);
}

export function fetchInstructorTasks(instructorId, tenantId) {
  return apiClient
    .get(`/api/v1/instructors/${instructorId}/tasks`, withTenant(tenantId))
    .then((r) => r.data);
}

export function fetchStudentCourseProgress(courseId, studentId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/students/${studentId}/progress`)
    .then((r) => r.data);
}

export function fetchStudentAttendance(courseId, studentId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/students/${studentId}/attendance`)
    .then((r) => r.data);
}

export function fetchCourseRoster(classId, { peersOnly = false } = {}) {
  return apiClient
    .get(`/api/v1/courses/${classId}/roster`, peersOnly ? { params: { peersOnly: true } } : undefined).then((r) => r.data);
}

// ── Course session & attendance endpoints ───────────────────────────────────

export function fetchSessionsForCourse(courseId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/sessions`)
    .then((r) => r.data);
}

export function fetchSessionOpenings(sessionId, tenantId) {
  return apiClient
    .get(`/api/v1/sessions/${sessionId}/openings`, withTenant(tenantId))
    .then((r) => r.data);
}

export function createSessionOpening(sessionId, tenantId, { teachingRole, assignedInstructorId, posted } = {}) {
  const body = { teachingRole };
  if (assignedInstructorId != null) body.assignedInstructorId = assignedInstructorId;
  if (posted) body.posted = true;
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/openings`, body, withTenant(tenantId))
    .then((r) => r.data);
}

export function updateSessionOpening(sessionId, openingId, tenantId, { teachingRole, assignedInstructorId } = {}) {
  const body = {};
  if (teachingRole != null) body.teachingRole = teachingRole;
  if (assignedInstructorId !== undefined) body.assignedInstructorId = assignedInstructorId;
  return apiClient
    .patch(`/api/v1/sessions/${sessionId}/openings/${openingId}`, body, withTenant(tenantId))
    .then((r) => r.data);
}

export function deleteSessionOpening(sessionId, openingId, tenantId) {
  return apiClient
    .delete(`/api/v1/sessions/${sessionId}/openings/${openingId}`, withTenant(tenantId))
    .then((r) => r.data);
}

export function requestSessionOpening(sessionId, openingId, tenantId) {
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/openings/${openingId}/request`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function dropSessionOpening(sessionId, openingId, tenantId) {
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/openings/${openingId}/drop`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

// Unlimited sign-ups with coordinator approval (opening.signupMode === "APPROVAL").
export function signUpForSessionOpening(sessionId, openingId, tenantId) {
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/openings/${openingId}/signup`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function withdrawOpeningSignup(sessionId, openingId, tenantId) {
  return apiClient
    .delete(`/api/v1/sessions/${sessionId}/openings/${openingId}/signup`, withTenant(tenantId))
    .then((r) => r.data);
}

export function fetchOpeningSignups(sessionId, openingId, tenantId) {
  return apiClient
    .get(`/api/v1/sessions/${sessionId}/openings/${openingId}/signups`, withTenant(tenantId))
    .then((r) => r.data);
}

export function approveOpeningSignup(sessionId, openingId, signupId, tenantId) {
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/openings/${openingId}/signups/${signupId}/approve`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

/** `note` is the optional comment for the instructor (max 500 characters); it is shown in the email and on My Requests. */
export function unapproveOpeningSignup(sessionId, openingId, signupId, tenantId, note) {
  const body = note && note.trim() ? { note } : {};
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/openings/${openingId}/signups/${signupId}/unapprove`, body, withTenant(tenantId))
    .then((r) => r.data);
}

/**
 * Whole-class sign-up: one call for several openings of one class, one email to the coordinators. Resolves with
 * { results: [{ openingId, status: CREATED | REFUSED | NOT_FOUND, reason, signupId? }] }.
 */
export function signUpForClassOpenings(classId, openingIds, tenantId) {
  return apiClient
    .post(`/api/v1/classes/${classId}/signups/batch`, { openingIds }, withTenant(tenantId))
    .then((r) => r.data);
}

/**
 * Coordinator: approve or unapprove several sign-ups at once; one email per instructor, course and role.
 * `action` is "APPROVE" or "UNAPPROVE"; `note` (optional, UNAPPROVE only) is the comment for the instructor.
 * Resolves with { results: [{ signupId, status: APPLIED | REFUSED | NOT_FOUND, reason }] }.
 */
export function decideInstructorSignups(signupIds, action, note, tenantId) {
  const body = { signupIds, action, ...(action === "UNAPPROVE" && note && note.trim() ? { note } : {}) };
  return apiClient
    .post("/api/v1/instructor-signups/decisions", body, withTenant(tenantId))
    .then((r) => r.data);
}

// Course Approvals, Instructor Sign-ups tab: every upcoming slot with sign-ups, grouped, with applicants.
export function fetchInstructorSignups(tenantId) {
  return apiClient
    .get("/api/v1/instructor-signups", withTenant(tenantId))
    .then((r) => r.data);
}

// signupLimit (optional, APPROVAL only): max pending + approved sign-ups; omit for unlimited.
export function setOpeningSignupMode(sessionId, openingId, tenantId, signupMode, signupLimit) {
  const body = signupLimit ? { signupMode, signupLimit } : { signupMode };
  return apiClient
    .patch(`/api/v1/sessions/${sessionId}/openings/${openingId}/signup-mode`, body, withTenant(tenantId))
    .then((r) => r.data);
}

// "Open to instructors": payload = { openings: { ROLE: n }, signupMode, signupLimit?, eligibleLevels? }.
export function postSessionOpenings(sessionId, tenantId, payload) {
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/openings/post`, payload, withTenant(tenantId))
    .then((r) => r.data);
}

// "Close sign-ups": hides the session's unassigned openings from instructors again.
export function closeSessionOpenings(sessionId, tenantId) {
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/openings/close`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function fetchMyOpenings(tenantId, from, to) {
  return apiClient
    .get(`/api/v1/instructors/me/openings`, mergeConfig(tenantId, { params: { from, to } }))
    .then((r) => r.data);
}

/** Read-only class summary (description, sessions, skill sheets, tests) for the teaching schedule's Details. */
export function fetchInstructorClassDetails(classId, tenantId) {
  return apiClient
    .get(`/api/v1/instructors/me/class-details/${classId}`, withTenant(tenantId))
    .then((r) => r.data);
}

export function fetchTenantLocations() {
  return apiClient.get(`/api/v1/tenant/locations`).then((r) => r.data);
}

export function createTenantLocation(location) {
  return apiClient
    .post(`/api/v1/tenant/locations`, location)
    .then((r) => r.data);
}

export function deleteTenantLocation(locationId) {
  return apiClient
    .delete(`/api/v1/tenant/locations/${locationId}`)
    .then((r) => r.data);
}

export function fetchLocationSuggestions(query) {
  return apiClient
    .get(`/api/v1/tenant/locations/suggestions`, { params: { q: query } })
    .then((r) => r.data);
}

export function createSession(courseId, data) {
  return apiClient
    .post(`/api/v1/courses/${courseId}/sessions`, data)
    .then((r) => r.data);
}

export function updateSession(sessionId, data) {
  return apiClient
    .patch(`/api/v1/sessions/${sessionId}`, data)
    .then((r) => r.data);
}

// ── QR check-in ──────────────────────────────────────────────────────────────

const positionBody = ({ lat, lng } = {}) => (lat != null && lng != null ? { lat, lng } : {});

/** A browser-bound handoff ticket (with its device secret) when there is one, else the scanned code itself. */
const credential = ({ token, handoff, device }) => (handoff ? { handoff, device } : { token });

/**
 * The session's current rotating check-in code. Coordinates, when given, are saved as the room's location.
 * `acknowledgeOffHours` records the instructor's acceptance of the risk of showing the code outside the session's day.
 */
export function createCheckinCode(sessionId, position, { acknowledgeOffHours = false } = {}) {
  const body = acknowledgeOffHours ? { ...positionBody(position), acknowledgeOffHours: true } : positionBody(position);
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/checkin-qr`, body)
    .then((r) => r.data);
}

/** A non-rotating code for printing. Every scan of the session must then be inside the room. */
export function createPrintedCheckinCode(sessionId, position) {
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/checkin-print`, positionBody(position))
    .then((r) => r.data);
}

/** { rosterPickEnabled, strictLocation, lateAfterMinutes (number or null) }. */
export function saveCheckinSettings(sessionId, settings) {
  return apiClient
    .put(`/api/v1/sessions/${sessionId}/checkin-settings`, settings)
    .then((r) => r.data);
}

/** Check-ins that deserve a second look: roster picks and ones outside the room. */
export function fetchCheckinReview(sessionId) {
  return apiClient.get(`/api/v1/sessions/${sessionId}/checkin-review`).then((r) => r.data);
}

/** action: "CONFIRM" or "REJECT". */
export function resolveCheckinReview(sessionId, attendanceId, action) {
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/checkin-review/${attendanceId}`, { action })
    .then((r) => r.data);
}

/** Public: trades a fresh scan for a ticket bound to this browser (via its device secret). */
export function checkinHandoff(token, device) {
  return apiClient
    .post("/api/v1/checkin/handoff", { token, device })
    .then((r) => r.data);
}

function checkinBody({ token, handoff, device, lat, lng }) {
  const body = {};
  if (token) body.token = token;
  if (handoff) {
    body.handoff = handoff;
    body.device = device;
  }
  if (lat != null && lng != null) {
    body.lat = lat;
    body.lng = lng;
  }
  return body;
}

/** Checks the signed-in student in with a fresh QR token, or with a handoff ticket plus this browser's device secret. */
export function checkIn(args) {
  return apiClient.post("/api/v1/checkin", checkinBody(args)).then((r) => r.data);
}

/** Records the signed-in student leaving; same arguments as checkIn. */
export function checkOut(args) {
  return apiClient.post("/api/v1/checkin/checkout", checkinBody(args)).then((r) => r.data);
}

/** Public, no login: the class roster for a pick. Only from inside the room, so a position is required. */
export function fetchCheckinRoster({ token, handoff, device, lat, lng }) {
  return apiClient
    .post("/api/v1/checkin/roster", { ...credential({ token, handoff, device }), ...positionBody({ lat, lng }) })
    .then((r) => r.data);
}

/** Public, no login: records the chosen student present (flagged for the instructor to review). */
export function pickRosterName({ token, handoff, userId, device, lat, lng }) {
  return apiClient
    .post("/api/v1/checkin/roster-pick", { ...credential({ token, handoff, device }), userId, device, ...positionBody({ lat, lng }) })
    .then((r) => r.data);
}

export function deleteSession(sessionId) {
  return apiClient.delete(`/api/v1/sessions/${sessionId}`).then((r) => r.data);
}

export function fetchAttendanceForSession(sessionId) {
  return apiClient
    .get(`/api/v1/sessions/${sessionId}/attendance`)
    .then((r) => r.data);
}

export function updateAttendanceRecord(sessionId, userId, data) {
  return apiClient
    .patch(`/api/v1/sessions/${sessionId}/attendance/${userId}`, data)
    .then((r) => r.data);
}
export function fetchMySessionAttendance(sessionId) {
  return apiClient
    .get(`/api/v1/sessions/${sessionId}/attendance/me`)
    .then((r) => r.data);
}
// ── Session content endpoints ─────────────────────────────────────────────

export function fetchSessionContent(sessionId) {
  return apiClient
    .get(`/api/v1/sessions/${sessionId}/content`)
    .then((r) => r.data);
}

export function addSessionContent(courseId, sessionId, data) {
  if (data.file) {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (k !== "file") fd.append(k, v);
    });
    fd.append("file", data.file);
    return apiClient
      .post(`/api/v1/courses/${courseId}/sessions/${sessionId}/content`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  }
  return apiClient
    .post(`/api/v1/courses/${courseId}/sessions/${sessionId}/content`, data)
    .then((r) => r.data);
}

export function updateSessionContent(contentId, data) {
  if (data.file) {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (k !== "file") fd.append(k, v);
    });
    fd.append("file", data.file);
    return apiClient
      .patch(`/api/v1/session-content/${contentId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  }
  return apiClient
    .patch(`/api/v1/session-content/${contentId}`, data)
    .then((r) => r.data);
}

export function deleteSessionContent(contentId) {
  return apiClient
    .delete(`/api/v1/session-content/${contentId}`)
    .then((r) => r.data);
}

/** Fetch class-level (not session-specific) documents/links for a course. */
export function fetchClassLevelContent(courseId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/content`)
    .then((r) => r.data);
}

/** Set (or overwrite) a student's private value for a per-student external link. Staff only — the response never includes the URL. */
export function setStudentContentLink(contentId, studentUserId, url) {
  return apiClient
    .put(`/api/v1/session-content/${contentId}/student-links/${studentUserId}`, { url })
    .then((r) => r.data);
}

/** Clear a student's private link value. Staff only. */
export function clearStudentContentLink(contentId, studentUserId) {
  return apiClient
    .delete(`/api/v1/session-content/${contentId}/student-links/${studentUserId}`)
    .then((r) => r.data);
}

/** List the class roster's link status for a per-student external link. Staff only — never includes URLs. */
export function fetchContentStudentLinks(contentId) {
  return apiClient
    .get(`/api/v1/session-content/${contentId}/student-links`)
    .then((r) => r.data);
}

/** Fetch the current student's own value for a per-student external link. */
export function fetchMyContentLink(contentId) {
  return apiClient
    .get(`/api/v1/session-content/${contentId}/my-link`)
    .then((r) => r.data);
}

/** Add a class-level (not session-specific) document/link to a course. */
export function addClassLevelContent(courseId, data) {
  if (data.file) {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (k !== "file") fd.append(k, v);
    });
    fd.append("file", data.file);
    return apiClient
      .post(`/api/v1/courses/${courseId}/content`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  }
  return apiClient
    .post(`/api/v1/courses/${courseId}/content`, data)
    .then((r) => r.data);
}

/** Private, coordinator-authored notes for a session's instructor(s). */
export function fetchSessionInstructorNotes(sessionId) {
  return apiClient
    .get(`/api/v1/sessions/${sessionId}/notes`)
    .then((r) => r.data.instructorNotes);
}

export function updateSessionInstructorNotes(sessionId, instructorNotes) {
  return apiClient
    .put(`/api/v1/sessions/${sessionId}/notes`, { instructorNotes })
    .then((r) => r.data.instructorNotes);
}

/** Download a content item's raw file. Returns a blob URL string. */
export function downloadContentFile(contentId) {
  return apiClient
    .get(`/api/v1/session-content/${contentId}/file`, { responseType: "blob" })
    .then((r) => URL.createObjectURL(r.data));
}

/**
 * Returns a URL the browser can stream/embed directly for a content item's
 * file (a presigned S3 URL in prod, or the proxied /file endpoint locally).
 */
export function getContentAccessUrl(contentId) {
  return apiClient
    .get(`/api/v1/session-content/${contentId}/access-url`)
    .then((r) => r.data.url);
}

/**
 * Move a content item to a different session or to class-level.
 * Pass sessionId = null to promote to class-level.
 */
export function moveSessionContent(contentId, sessionId) {
  return apiClient
    .patch(`/api/v1/session-content/${contentId}/move`, {
      sessionId: sessionId ?? null,
    })
    .then((r) => r.data);
}

/** Persists a new display order for a session's content items. */
export function reorderSessionContent(sessionId, contentIds) {
  return apiClient
    .patch(`/api/v1/sessions/${sessionId}/content/reorder`, { contentIds })
    .then((r) => r.data);
}

/** Persists a new display order for a course's class-level content items. */
export function reorderClassContent(courseId, contentIds) {
  return apiClient
    .patch(`/api/v1/classes/${courseId}/content/reorder`, { contentIds })
    .then((r) => r.data);
}

// ── Learning path endpoints (replaces single "learning plan" with multiple named paths) ─────

/** List all learning paths (named learning plans) for a course. */
export function fetchLearningPaths(courseId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/learning-plan-paths`)
    .then((r) => r.data);
}

/** Create a new named learning path in a course. */
export function createLearningPath(courseId, { name, description, sessionId = null }) {
  return apiClient
    .post(`/api/v1/courses/${courseId}/learning-plan-paths`, { name, description, sessionId })
    .then((r) => r.data);
}

/** Moves a learning path to Global (sessionId=null) or a specific session. */
export function setLearningPathSession(courseId, planId, sessionId) {
  return apiClient
    .patch(`/api/v1/courses/${courseId}/learning-plan-paths/${planId}/session`, { sessionId })
    .then((r) => r.data);
}

/** Rename or update description of an existing learning path. */
export function renameLearningPath(courseId, planId, { name, description }) {
  return apiClient
    .patch(`/api/v1/courses/${courseId}/learning-plan-paths/${planId}`, {
      name,
      description,
    })
    .then((r) => r.data);
}

/** Delete a learning path (cascade-deletes its items and views). */
export function deleteLearningPath(courseId, planId) {
  return apiClient
    .delete(`/api/v1/courses/${courseId}/learning-plan-paths/${planId}`)
    .then((r) => r.data);
}

/** Full learning path + items for a specific path (coordinator/instructor view). */
export function fetchLearningPlan(courseId, planId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/learning-plan-paths/${planId}`)
    .then((r) => r.data);
}

/** Enabled Document/Test/Skill Sheet assignments not yet in a specific path. */
export function fetchAvailablePlanContent(courseId, planId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/learning-plan-paths/${planId}/available-content`)
    .then((r) => r.data);
}

/** Appends a content assignment to the end of a learning path. */
export function addLearningPlanItem(
  courseId,
  planId,
  { contentType, contentRefId },
) {
  return apiClient
    .post(`/api/v1/courses/${courseId}/learning-plan-paths/${planId}/items`, {
      contentType,
      contentRefId,
    })
    .then((r) => r.data);
}

export function removeLearningPlanItem(courseId, planId, itemId) {
  return apiClient
    .delete(`/api/v1/courses/${courseId}/learning-plan-paths/${planId}/items/${itemId}`)
    .then((r) => r.data);
}

/** Persists a new display order for a path's items. */
export function reorderLearningPlanItems(courseId, planId, itemIds) {
  return apiClient
    .patch(`/api/v1/courses/${courseId}/learning-plan-paths/${planId}/items/reorder`, {
      itemIds,
    })
    .then((r) => r.data);
}

export function setItemGating(courseId, planId, itemId, requiresPreviousComplete) {
  return apiClient
    .patch(
      `/api/v1/courses/${courseId}/learning-plan-paths/${planId}/items/${itemId}/gating`,
      {
        requiresPreviousComplete,
      },
    )
    .then((r) => r.data);
}

export function setItemDualShow(courseId, planId, itemId, dualShow) {
  return apiClient
    .patch(
      `/api/v1/courses/${courseId}/learning-plan-paths/${planId}/items/${itemId}/dual-show`,
      {
        dualShow,
      },
    )
    .then((r) => r.data);
}

/** pacingMode: "SELF_PACED" | "INSTRUCTOR_PACED" */
export function setPlanPacing(courseId, planId, pacingMode) {
  return apiClient
    .patch(`/api/v1/courses/${courseId}/learning-plan-paths/${planId}/pacing`, {
      pacingMode,
    })
    .then((r) => r.data);
}

/** Advances the instructor-paced unlock cursor by one item. */
export function unlockNextPlanItem(courseId, planId) {
  return apiClient
    .post(`/api/v1/courses/${courseId}/learning-plan-paths/${planId}/unlock-next`)
    .then((r) => r.data);
}

/** Student's view of all paths in a course: each path with items + completion %. */
export function fetchMyLearningPlan(courseId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/learning-plan-paths/my-view`)
    .then((r) => r.data);
}

/** Marks a document plan item as opened/viewed for the current student. */
export function markPlanItemViewed(courseId, planId, itemId) {
  return apiClient
    .post(
      `/api/v1/courses/${courseId}/learning-plan-paths/${planId}/items/${itemId}/mark-viewed`,
    )
    .then((r) => r.data);
}

/** Marks a document plan item as fully read (reached the end). */
export function markPlanItemReachedEnd(courseId, planId, itemId) {
  return apiClient
    .post(
      `/api/v1/courses/${courseId}/learning-plan-paths/${planId}/items/${itemId}/reached-end`,
    )
    .then((r) => r.data);
}

/**
 * Student self check-in. coords is optional: { lat, lng }
 */
export function studentCheckIn(sessionId, coords = null) {
  const body = coords ? { lat: coords.lat, lng: coords.lng } : {};
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/checkin`, body)
    .then((r) => r.data);
}

/**
 * Student self check-out. coords is optional: { lat, lng }
 */
export function studentCheckOut(sessionId, coords = null) {
  const body = coords ? { lat: coords.lat, lng: coords.lng } : {};
  return apiClient
    .post(`/api/v1/sessions/${sessionId}/checkout`, body)
    .then((r) => r.data);
}

// ── Course checklist endpoints ─────────────────────────────────────────────

export function fetchChecklistsForCourse(courseId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/checklists`)
    .then((r) => r.data);
}

/** Fetch checklists assigned to a specific session. */
export function fetchChecklistsForSession(courseId, sessionId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/sessions/${sessionId}/checklists`)
    .then((r) => r.data);
}

/** Create a class-level checklist. */
export function createChecklist(courseId, data) {
  return apiClient
    .post(`/api/v1/courses/${courseId}/checklists`, data)
    .then((r) => r.data);
}

/** Create a checklist assigned to a specific session. */
export function createSessionChecklist(courseId, sessionId, data) {
  return apiClient
    .post(`/api/v1/courses/${courseId}/sessions/${sessionId}/checklists`, data)
    .then((r) => r.data);
}

/**
 * Move a checklist to a different session or to class-level.
 * Pass sessionId = null to promote to class-level.
 */
export function moveChecklist(checklistId, sessionId) {
  return apiClient
    .patch(`/api/v1/checklists/${checklistId}/move`, {
      sessionId: sessionId ?? null,
    })
    .then((r) => r.data);
}

/** Persists a new display order for a course's class-level skill checklists. */
export function reorderClassChecklists(courseId, checklistIds) {
  return apiClient
    .patch(`/api/v1/classes/${courseId}/checklists/reorder`, { checklistIds })
    .then((r) => r.data);
}

/** Persists a new display order for one session's skill checklists. */
export function reorderSessionChecklists(courseId, sessionId, checklistIds) {
  return apiClient
    .patch(`/api/v1/sessions/${sessionId}/checklists/reorder`, { checklistIds })
    .then((r) => r.data);
}

export function updateChecklist(checklistId, data) {
  return apiClient
    .patch(`/api/v1/checklists/${checklistId}`, data)
    .then((r) => r.data);
}

export function deleteChecklist(checklistId) {
  return apiClient
    .delete(`/api/v1/checklists/${checklistId}`)
    .then((r) => r.data);
}

export function fetchChecklist(checklistId) {
  return apiClient.get(`/api/v1/checklists/${checklistId}`).then((r) => r.data);
}

export function fetchChecklistEvaluation(courseId, checklistId, studentId) {
  return apiClient
    .get(
      `/api/v1/courses/${courseId}/checklists/${checklistId}/evaluations/${studentId}`,
    )
    .then((r) => r.data);
}

export function saveEvaluationDraft(courseId, checklistId, studentId, data) {
  return apiClient
    .put(
      `/api/v1/courses/${courseId}/checklists/${checklistId}/evaluations/${studentId}/draft`,
      data,
    )
    .then((r) => r.data);
}

export function submitInstructorSignOff(
  courseId,
  checklistId,
  studentId,
  data,
) {
  return apiClient
    .post(
      `/api/v1/courses/${courseId}/checklists/${checklistId}/evaluations/${studentId}/signoff`,
      data,
    )
    .then((r) => r.data);
}

/** Fetch completion progress for all students across all checklists in a course. */
export function fetchChecklistProgress(courseId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/checklists/progress`)
    .then((r) => r.data);
}

/**
 * Practice attempts — unlimited peer-run practice for a skill checklist,
 * separate from the official instructor-graded evaluation.
 */
export function fetchPracticeAttempts(checklistId) {
  return apiClient
    .get(`/api/v1/skill-checklists/${checklistId}/practice-attempts`)
    .then((r) => r.data);
}

export function createPracticeAttempt(checklistId, data) {
  return apiClient
    .post(`/api/v1/skill-checklists/${checklistId}/practice-attempts`, data)
    .then((r) => r.data);
}

// ── Written Test endpoints ───────────────────────────────────────────────────

/** Fetch a single test by ID. */
export function fetchTestById(testId) {
  return apiClient.get(`/api/v1/tests/${testId}`).then((r) => r.data);
}

/** Fetch class-level tests for a course. */
export function fetchTestsForCourse(courseId) {
  return apiClient.get(`/api/v1/courses/${courseId}/tests`).then((r) => r.data);
}

/** Fetch tests assigned to a specific session. */
export function fetchTestsForSession(courseId, sessionId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/sessions/${sessionId}/tests`)
    .then((r) => r.data);
}

/** Create a class-level test. */
export function createTest(courseId, data) {
  return apiClient
    .post(`/api/v1/courses/${courseId}/tests`, data)
    .then((r) => r.data);
}

/** Create a test assigned to a specific session. */
export function createSessionTest(courseId, sessionId, data) {
  return apiClient
    .post(`/api/v1/courses/${courseId}/sessions/${sessionId}/tests`, data)
    .then((r) => r.data);
}

/** Update an existing test (title, description, questions, passingScore, timeLimitMinutes). */
export function updateTest(testId, data) {
  return apiClient.patch(`/api/v1/tests/${testId}`, data).then((r) => r.data);
}

/** Delete a test and all its submissions. */
export function deleteTest(testId) {
  return apiClient.delete(`/api/v1/tests/${testId}`).then((r) => r.data);
}

/** Enable or disable a test (controls student visibility). */
export function enableTest(testId, enabled) {
  return apiClient
    .patch(`/api/v1/tests/${testId}/enable`, { enabled })
    .then((r) => r.data);
}

/** Lock or unlock a test (controls whether submissions are accepted). */
export function lockTest(testId, locked) {
  return apiClient
    .patch(`/api/v1/tests/${testId}/lock`, { locked })
    .then((r) => r.data);
}

/**
 * Move a test to a different session or to class-level.
 * Pass sessionId = null to promote to class-level.
 */
export function moveTest(testId, sessionId) {
  return apiClient
    .patch(`/api/v1/tests/${testId}/move`, { sessionId: sessionId ?? null })
    .then((r) => r.data);
}

/** Persists a new display order for a course's class-level tests. */
export function reorderClassTests(courseId, testIds) {
  return apiClient
    .patch(`/api/v1/classes/${courseId}/tests/reorder`, { testIds })
    .then((r) => r.data);
}

/** Persists a new display order for one session's tests. */
export function reorderSessionTests(courseId, sessionId, testIds) {
  return apiClient
    .patch(`/api/v1/sessions/${sessionId}/tests/reorder`, { testIds })
    .then((r) => r.data);
}

/** Student: start a test (records startedAt, returns questions without answers). */
export function startTest(testId, studentId) {
  return apiClient
    .post(`/api/v1/tests/${testId}/start`, { studentId })
    .then((r) => r.data);
}

/** Student: submit answers. Returns auto-graded results immediately. */
export function submitTest(testId, studentId, answers) {
  return apiClient
    .post(`/api/v1/tests/${testId}/submit`, { studentId, answers })
    .then((r) => r.data);
}

/** Get a single student's submission for a test. */
export function fetchTestSubmission(testId, studentId) {
  return apiClient
    .get(`/api/v1/tests/${testId}/submission/${studentId}`)
    .then((r) => r.data);
}

/** Get the latest completed submission for a student (for viewing results). */
export function fetchLatestTestSubmission(testId, studentId) {
  return apiClient
    .get(`/api/v1/tests/${testId}/latest-submission/${studentId}`)
    .then((r) => r.data)
    .catch(() => null);
}

/** Instructor: get all submissions for a test. */
export function fetchTestResults(testId) {
  return apiClient.get(`/api/v1/tests/${testId}/results`).then((r) => r.data);
}

/** Instructor: get per-student aggregated summaries for the assess page. */
export function fetchStudentSummaries(testId) {
  return apiClient
    .get(`/api/v1/tests/${testId}/student-summaries`)
    .then((r) => r.data);
}

/** Instructor: grant one additional retake to a specific student. */
export function grantRetake(testId, studentId) {
  return apiClient
    .post(`/api/v1/tests/${testId}/retake-grant/${studentId}`)
    .then((r) => r.data);
}

/** Instructor: get aggregate progress for all tests in a course. */
export function fetchTestProgress(courseId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/tests/progress`)
    .then((r) => r.data);
}

// ── Registration endpoints (no tenant in path) ──────────────────────────────

/**
 * Register a student for a class, completing the 3-step registration wizard.
 *
 * @param {number} classId
 * @param {number} userId
 * @param {object} wizardData - Wizard results:
 *   - prerequisitesConfirmed {boolean}
 *   - termsAccepted {boolean}
 *   - paymentIntentId {string|null} - from createPaymentIntent(); null when free
 */
export function registerForClass(classId, userId, wizardData = {}) {
  return apiClient
    .post(`/api/v1/classes/${classId}/registrations`, {
      userId,
      ...wizardData,
    })
    .then((r) => r.data);
}

/**
 * Determine whether payment is required for the current user to enroll in a class,
 * and get a (mock) payment intent that will be passed back during registration.
 *
 * Response shape:
 *   { required: boolean, amount: number, currency: string,
 *     paymentIntentId: string|null, clientSecret: string|null }
 *
 * TODO [STRIPE]: When Stripe is integrated, use the returned `clientSecret`
 * with Stripe.js / React Stripe Elements (<PaymentElement />) to collect payment.
 * The frontend should call stripe.confirmPayment({ clientSecret }) after the user
 * enters their card. Do NOT send card details to your own server.
 * See: https://stripe.com/docs/payments/accept-a-payment
 *
 * @param {number} classId
 */
export function createPaymentIntent(classId) {
  return apiClient
    .post(`/api/v1/classes/${classId}/payment-intent`, {})
    .then((r) => r.data);
}

/**
 * Look up a user by email address. Returns an array (0 or 1 element).
 */
export function lookupUserByEmail(email) {
  return apiClient
    .post(`/api/v1/users/lookup`, { email })
    .then(unwrapDataOrArray);
}

/**
 * COURSE_COORDINATOR / ADMIN: register a student directly, bypassing the wizard.
 */
export function adminRegisterForClass(classId, userId) {
  return apiClient
    .post(`/api/v1/classes/${classId}/registrations/direct`, { userId })
    .then((r) => r.data);
}

/**
 * AGENCY_ADMIN: register multiple agency members for a class in one wizard flow.
 * wizardData: { prerequisitesConfirmed, termsAccepted, paymentIntentId }
 */
export function bulkRegisterForClass(
  classId,
  userIds,
  agencyId,
  wizardData = {},
) {
  return apiClient
    .post(`/api/v1/classes/${classId}/registrations/bulk`, {
      userIds,
      agencyId,
      ...wizardData,
    })
    .then((r) => r.data);
}

export function fetchRegistrationsForClass(classId) {
  return apiClient
    .get(`/api/v1/classes/${classId}/registrations`)
    .then((r) => r.data);
}

export function fetchInstructorDashboard(classId) {
  return apiClient
    .get(`/api/v1/classes/${classId}/instructor-dashboard`)
    .then((r) => r.data);
}

// ── AHA eCards (class-level configuration, roster eligibility, submission) ──

export function getAhaCatalog() {
  return apiClient.get(`/api/v1/aha/catalog`).then((r) => r.data);
}

export function getAhaProductIdentifiers(productKey) {
  return apiClient.get(`/api/v1/aha/catalog/products/${encodeURIComponent(productKey)}/identifiers`).then((r) => r.data);
}

// GLOBAL_ADMIN: verify/activate a product for the environment this backend is connected to. Send only the flags to change.
export function setAhaProductEnvironmentStatus(productKey, environment, status) {
  return apiClient.put(`/api/v1/aha/catalog/products/${encodeURIComponent(productKey)}/environments/${encodeURIComponent(environment)}`,
    status).then((r) => r.data);
}

// GLOBAL_ADMIN catalog management (connected environment only). Products/modules are never deleted, only deactivated.
export function getAhaCatalogAdmin() {
  return apiClient.get(`/api/v1/aha/catalog/admin/products`).then((r) => r.data);
}

export function getAhaModulesAdmin() {
  return apiClient.get(`/api/v1/aha/catalog/admin/modules`).then((r) => r.data);
}

export function createAhaProduct(body) {
  return apiClient.post(`/api/v1/aha/catalog/products`, body).then((r) => r.data);
}

export function updateAhaProduct(productKey, body) {
  return apiClient.put(`/api/v1/aha/catalog/products/${encodeURIComponent(productKey)}`, body).then((r) => r.data);
}

export function setAhaProductModules(productKey, body) {
  return apiClient.put(`/api/v1/aha/catalog/products/${encodeURIComponent(productKey)}/modules`, body).then((r) => r.data);
}

export function createAhaModule(body) {
  return apiClient.post(`/api/v1/aha/catalog/modules`, body).then((r) => r.data);
}

export function updateAhaModule(moduleKey, body) {
  return apiClient.put(`/api/v1/aha/catalog/modules/${encodeURIComponent(moduleKey)}`, body).then((r) => r.data);
}

export function correctAhaProductNumber(productKey, value, expectedValue, reason) {
  return apiClient.put(`/api/v1/aha/catalog/products/${encodeURIComponent(productKey)}/number`,
    { value, expectedValue, reason }).then((r) => r.data);
}

export function correctAhaModuleId(productKey, moduleKey, value, expectedValue, reason) {
  return apiClient.put(`/api/v1/aha/catalog/products/${encodeURIComponent(productKey)}/modules/${encodeURIComponent(moduleKey)}/id`,
    { value, expectedValue, reason }).then((r) => r.data);
}

export function getAhaEligibility(classId) {
  return apiClient
    .get(`/api/v1/classes/${classId}/aha/eligibility`)
    .then((r) => r.data);
}

// The class's AHA setup checklist: center, certificate, course, session modules, instructor AHA IDs.
export function getAhaClassSetup(classId) {
  return apiClient
    .get(`/api/v1/classes/${classId}/aha/setup`)
    .then((r) => r.data);
}

export function saveAhaClassConfig(classId, config) {
  return apiClient
    .put(`/api/v1/classes/${classId}/aha/config`, config)
    .then((r) => r.data);
}

// Remaining AHA eCard inventory for the current training center (coordinator dashboard):
// { environment, products: [{ productKey, productName, productNumber, availableBalance, error }], observedAt, error }.
export function fetchAhaInventory(refresh = false) {
  return apiClient.get(`/api/v1/aha/inventory`, { params: { refresh } }).then((r) => r.data);
}

// The signed-in user's own issued AHA eCards: [{ id, productName, classTitle, eCardCode, claimUrl, issuedAt, environment }].
export function fetchMyAhaEcards() {
  return apiClient.get(`/api/v1/aha/my-ecards`).then((r) => r.data);
}

// Coordinator/admin: record what AHA's own records show for a request whose result is unknown.
// outcome is "ASSIGNED" (with the eCard code, claim link optional) or "REJECTED" (AHA has no card).
/** A card request that looks stuck: a queued one is sent again; a sending one becomes "result unknown" to check. */
export function recheckAhaIssuance(classId, registrationId) {
  return apiClient.post(`/api/v1/classes/${classId}/aha/issuances/${registrationId}/recheck`).then((r) => r.data);
}

export function resolveAhaIssuance(classId, registrationId, { outcome, ecardCode, claimUrl, note }) {
  return apiClient
    .post(`/api/v1/classes/${classId}/aha/issuances/${registrationId}/resolve`, { outcome, ecardCode, claimUrl, note })
    .then((r) => r.data);
}

// Coordinator/admin: every AHA card request in the current training center (no card codes).
export function fetchAhaIssuanceReport() {
  return apiClient.get(`/api/v1/aha/issuances`).then((r) => r.data);
}

// ── Certificates ────────────────────────────────────────────────────────────────────────────────────────────────
// The center's certificate types: [{ type: "DEFAULT"|"AHA", name, enabled, settings, instructorIdType }].
// settings is only sent to admins (null for coordinators).
export function fetchCertificateTypes() {
  return apiClient.get(`/api/v1/tenant/certificate-types`).then((r) => r.data);
}

// Admin: add or update one certificate type. Omit enabled or settings to keep the current value.
export function saveCertificateType(type, { enabled, settings }) {
  return apiClient.put(`/api/v1/tenant/certificate-types/${type}`, { enabled, settings }).then((r) => r.data);
}

// { ids: { AHA_INSTRUCTOR: { "<userId>": "<id>" | null } }, valuesHidden } — coordinators get null values.
export function fetchTenantInstructorIds() {
  return apiClient.get(`/api/v1/tenant/instructor-ids`).then((r) => r.data);
}

// Admin: one instructor's IDs for this center: { userId, ids: { AHA_INSTRUCTOR: "5180685540" } }.
export function fetchInstructorExternalIds(userId) {
  return apiClient.get(`/api/v1/instructors/${userId}/external-ids`).then((r) => r.data);
}

// Admin: set (or, with a blank value, remove) an instructor's ID for this center. Returns { userId, ids }.
export function saveInstructorExternalId(userId, type, value) {
  return apiClient.put(`/api/v1/instructors/${userId}/external-ids`, { type, value }).then((r) => r.data);
}

// { types: ["DEFAULT", "AHA"], available: [{ type, name, instructorIdType }] }
export function fetchClassCertificateTypes(classId) {
  return apiClient.get(`/api/v1/classes/${classId}/certificate-types`).then((r) => r.data);
}

export function saveClassCertificateTypes(classId, types) {
  return apiClient.put(`/api/v1/classes/${classId}/certificate-types`, { types }).then((r) => r.data);
}

export function fetchTemplateCertificateTypes(templateId) {
  return apiClient.get(`/api/v1/templates/${templateId}/certificate-types`).then((r) => r.data);
}

export function saveTemplateCertificateTypes(templateId, types) {
  return apiClient.put(`/api/v1/templates/${templateId}/certificate-types`, { types }).then((r) => r.data);
}

// What pressing Send certificates would do, and where each certificate stands:
// { classComplete, canSend, notReady, willSend, types: [{ type, name, classBlockers, willSend,
//   students: [{ registrationId, userId, name, status, retry, reasons }] }] }
export function fetchCertificateSends(classId) {
  return apiClient.get(`/api/v1/classes/${classId}/certificate-sends`).then((r) => r.data);
}

// Sends every certificate still owed on the class (no picking). Returns the same summary afterwards.
/** @param overrides [{registrationId, reason}]: coordinator/admin only, held-back AHA cards to send anyway. */
export function sendClassCertificates(classId, overrides = []) {
  return apiClient.post(`/api/v1/classes/${classId}/certificate-sends`, { attested: true, overrides }).then((r) => r.data);
}

export function updateRegistrationStatus(classId, regId, updates) {
  return apiClient
    .patch(`/api/v1/classes/${classId}/registrations/${regId}`, updates)
    .then((r) => r.data);
}

export function dropRegistration(classId, registrationId, acknowledged = false) {
  return apiClient
    .delete(`/api/v1/classes/${classId}/registrations/${registrationId}`, {
      data: { acknowledged },
    })
    .then((r) => r.data);
}

export function exportToAuthority(classId, data) {
  return apiClient
    .post(`/api/v1/classes/${classId}/exports`, data)
    .then((r) => r.data);
}

export function fetchRegistrationsForUser(userId, tenantId) {
  return apiClient
    .get(`/api/v1/users/${userId}/registrations`, mergeConfig(tenantId))
    .then((r) => r.data);
}

// ── User certification endpoints (no tenant) ─────────────────────────────────

export function fetchUserCertifications(userId) {
  return apiClient
    .get(`/api/v1/users/${userId}/certifications`)
    .then((r) => r.data);
}

export function createCertification(userId, cert) {
  // If there's a file to upload, use FormData
  if (cert.documentFile) {
    const formData = new FormData();
    formData.append("certTypeId", cert.certTypeId || "");
    formData.append("name", cert.name || "");
    formData.append("issuer", cert.issuer || "");
    formData.append("certNumber", cert.certNumber || "");
    formData.append("issueDate", cert.issueDate || "");
    formData.append("expires", cert.expires || "");
    formData.append("documentName", cert.documentName || "");
    formData.append("evidenceUrl", cert.evidenceUrl || "");
    formData.append("document", cert.documentFile);

    return apiClient
      .post(`/api/v1/users/${userId}/certifications`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  }

  // Otherwise, use regular JSON
  return apiClient
    .post(`/api/v1/users/${userId}/certifications`, cert)
    .then((r) => r.data);
}

export function updateCertification(userId, certId, cert) {
  // If there's a new file to upload, use FormData
  if (cert.documentFile) {
    const formData = new FormData();
    formData.append("certTypeId", cert.certTypeId || "");
    formData.append("name", cert.name || "");
    formData.append("issuer", cert.issuer || "");
    formData.append("certNumber", cert.certNumber || "");
    formData.append("issueDate", cert.issueDate || "");
    formData.append("expires", cert.expires || "");
    formData.append("documentName", cert.documentName || "");
    formData.append("evidenceUrl", cert.evidenceUrl || "");
    formData.append("document", cert.documentFile);

    return apiClient
      .patch(`/api/v1/users/${userId}/certifications/${certId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  }

  // Otherwise, use regular JSON (allows updating without file)
  return apiClient
    .patch(`/api/v1/users/${userId}/certifications/${certId}`, cert)
    .then((r) => r.data);
}

export function deleteCertification(userId, certId) {
  return apiClient
    .delete(`/api/v1/users/${userId}/certifications/${certId}`)
    .then((r) => r.data);
}

export function fetchExpiringCertifications(userId, withinDays) {
  return apiClient
    .get(`/api/v1/users/${userId}/certifications/renewals`, {
      params: { withinDays },
    })
    .then((r) => r.data);
}

export function fetchCourseCompletionsForUser(userId) {
  return apiClient
    .get(`/api/v1/users/${userId}/course-completions`)
    .then((r) => r.data);
}

export function downloadCertificationDocument(userId, certId) {
  return apiClient
    .get(`/api/v1/users/${userId}/certifications/${certId}/document`, {
      responseType: "blob",
    })
    .then((r) => r.data);
}

export function downloadCertificatePdf(certId) {
  return apiClient
    .get(`/api/v1/certificates/${certId}/pdf`, { responseType: "blob" })
    .then((r) => r.data);
}

export function fetchCompletedCourses(studentId) {
  return apiClient
    .get(`/api/v1/students/${studentId}/completed-courses`)
    .then((r) => r.data)
    .catch(() => []);
}

export function fetchCertificationsByUserIds(userIds, tenantId) {
  const q = Array.isArray(userIds) ? userIds.join(",") : userIds;
  return apiClient
    .get(
      `/api/v1/certifications`,
      mergeConfig(tenantId, { params: { userIds: q } }),
    )
    .then((r) => unwrapDataOrArray(r));
}

export function fetchPendingCourseSubmissions(status, tenantId) {
  const config = mergeConfig(tenantId, { params: { status } });
  return apiClient
    .get(`/api/v1/course-submissions`, config)
    .then((r) => unwrapDataOrArray(r));
}

export function fetchInstructorSubmissions(instructorId) {
  return apiClient
    .get(`/api/v1/course-submissions`, {
      params: { instructorId },
    })
    .then((r) => unwrapDataOrArray(r));
}

export function approveCourseSubmission(submissionId) {
  return apiClient
    .post(`/api/v1/course-submissions/${submissionId}/approve`)
    .then((r) => r.data);
}

export function rejectCourseSubmission(submissionId, reason) {
  return apiClient
    .post(`/api/v1/course-submissions/${submissionId}/reject`, {
      reason,
    })
    .then((r) => r.data);
}

export function fetchPendingRegistrationApprovals() {
  return apiClient
    .get(`/api/v1/registrations/pending-approval`)
    .then((r) => unwrapDataOrArray(r));
}

export function approveRegistration(classId, registrationId) {
  return apiClient
    .post(`/api/v1/classes/${classId}/registrations/${registrationId}/approve`)
    .then((r) => r.data);
}

export function rejectRegistration(classId, registrationId) {
  return apiClient
    .post(`/api/v1/classes/${classId}/registrations/${registrationId}/reject`)
    .then((r) => r.data);
}

export function cancelCourseSubmission(submissionId) {
  return apiClient
    .post(`/api/v1/course-submissions/${submissionId}/cancel`)
    .then((r) => r.data);
}

export function submitCourseForApproval(submission) {
  return apiClient
    .post(`/api/v1/course-submissions`, submission)
    .then((r) => r.data);
}

export function updateSubmissionInstructors(submissionId, instructorIds) {
  return apiClient
    .patch(`/api/v1/course-submissions/${submissionId}/instructors`, {
      instructorIds,
    })
    .then((r) => r.data);
}

// ── Semester endpoints ────────────────────────────────────────────────────────

export function fetchSemesters(tenantId) {
  return apiClient
    .get(`/api/v1/semesters`, withTenant(tenantId))
    .then((r) => r.data);
}

export function fetchSemester(id, tenantId) {
  return apiClient
    .get(`/api/v1/semesters/${id}`, withTenant(tenantId))
    .then((r) => r.data);
}

export function createSemester(data, tenantId) {
  return apiClient
    .post(`/api/v1/semesters`, data, withTenant(tenantId))
    .then((r) => r.data);
}

export function updateSemester(id, data, tenantId) {
  return apiClient
    .patch(`/api/v1/semesters/${id}`, data, withTenant(tenantId))
    .then((r) => r.data);
}

export function deleteSemester(id, tenantId) {
  return apiClient
    .delete(`/api/v1/semesters/${id}`, withTenant(tenantId))
    .then((r) => r.data);
}

export function addSemesterClass(semesterId, data, tenantId) {
  return apiClient
    .post(`/api/v1/semesters/${semesterId}/classes`, data, withTenant(tenantId))
    .then((r) => r.data);
}

export function updateSemesterClass(semesterId, classId, data, tenantId) {
  return apiClient
    .patch(
      `/api/v1/semesters/${semesterId}/classes/${classId}`,
      data,
      withTenant(tenantId),
    )
    .then((r) => r.data);
}

export function removeSemesterClass(semesterId, classId, tenantId) {
  return apiClient
    .delete(
      `/api/v1/semesters/${semesterId}/classes/${classId}`,
      withTenant(tenantId),
    )
    .then((r) => r.data);
}

export function publishSemester(id, tenantId) {
  return apiClient
    .post(`/api/v1/semesters/${id}/publish`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

export function revertSemester(id, tenantId) {
  return apiClient
    .post(`/api/v1/semesters/${id}/revert`, {}, withTenant(tenantId))
    .then((r) => r.data);
}

// ── Certification type (allowed certifications) endpoints (tenant required) ──

export function fetchAllowedCertifications(tenantId) {
  return apiClient
    .get(`/api/v1/certification-types`, withTenant(tenantId))
    .then((r) => r.data);
}

export function createAllowedCertification(tenantId, cert) {
  return apiClient
    .post(`/api/v1/certification-types`, cert, withTenant(tenantId))
    .then((r) => r.data);
}

export function updateAllowedCertification(tenantId, certTypeId, cert) {
  return apiClient
    .patch(
      `/api/v1/certification-types/${certTypeId}`,
      cert,
      withTenant(tenantId),
    )
    .then((r) => r.data);
}

export function deleteAllowedCertification(tenantId, certTypeId) {
  return apiClient
    .delete(`/api/v1/certification-types/${certTypeId}`, withTenant(tenantId))
    .then((r) => r.data);
}

export function fetchNextClassesForAllowedCerts(tenantId) {
  return apiClient
    .get(`/api/v1/certification-types/renewal-classes`, withTenant(tenantId))
    .then((r) => r.data);
}

// ── Configurable dropdown lists (tenant required via header) ─────────────────

export function fetchConfigList(tenantId, listKey) {
  return apiClient
    .get(`/api/v1/config-lists/${listKey}`, withTenant(tenantId))
    .then((r) => r.data);
}

export function createConfigListItem(tenantId, listKey, item) {
  return apiClient
    .post(`/api/v1/config-lists/${listKey}`, item, withTenant(tenantId))
    .then((r) => r.data);
}

export function updateConfigListItem(tenantId, listKey, id, item) {
  return apiClient
    .patch(`/api/v1/config-lists/${listKey}/${id}`, item, withTenant(tenantId))
    .then((r) => r.data);
}

export function deleteConfigListItem(tenantId, listKey, id) {
  return apiClient
    .delete(`/api/v1/config-lists/${listKey}/${id}`, withTenant(tenantId))
    .then((r) => r.data);
}

// ── Agency endpoints (tenant required via header) ────────────────────────────

export function fetchAgencies(tenantId) {
  return apiClient
    .get(`/api/v1/agencies`, withTenant(tenantId))
    .then((r) => r.data);
}

export function createAgency(tenantId, agency) {
  return apiClient
    .post(`/api/v1/agencies`, agency, withTenant(tenantId))
    .then((r) => r.data);
}

export function updateAgency(tenantId, agencyId, agency) {
  return apiClient
    .patch(`/api/v1/agencies/${agencyId}`, agency, withTenant(tenantId))
    .then((r) => r.data);
}

export function deleteAgency(tenantId, agencyId) {
  return apiClient
    .delete(`/api/v1/agencies/${agencyId}`, withTenant(tenantId))
    .then((r) => r.data);
}

// ── Agency membership endpoints (tenant required via header) ─────────────────

export function fetchUserMemberships(userId) {
  return apiClient
    .get(`/api/v1/users/${userId}/agency-memberships`)
    .then((r) => r.data);
}

/** The user's agency affiliations in every training center where they hold a role, tagged with tenantName. */
export function fetchUserAgencies(userId) {
  return apiClient.get(`/api/v1/users/${userId}/agencies`).then((r) => r.data);
}

export function acceptAgencyInvite(userId, membershipId) {
  return apiClient
    .post(`/api/v1/users/${userId}/agency-memberships/${membershipId}/accept`)
    .then((r) => r.data);
}

export function fetchAgencyMemberships(tenantId, agencyId) {
  return apiClient
    .get(`/api/v1/agencies/${agencyId}/memberships`, withTenant(tenantId))
    .then((r) => r.data);
}

// ── Admin Tenant Management endpoints ──────────────────────────────────────

/**
 * Admin: fetch all tenants.
 */
export function fetchAdminAllTenants() {
  return apiClient.get(`/api/v1/admin/tenants`).then((r) => r.data);
}

/**
 * Admin: create a new tenant.
 *
 * @param {object} tenantData - { id, name, logo, icon, theme, features }
 */
export function createNewTenant(tenantData) {
  return apiClient
    .post(`/api/v1/admin/tenants`, tenantData)
    .then((r) => r.data);
}

/**
 * Admin: update tenant details (name, logo, icon, theme, features).
 *
 * @param {string} tenantId - Tenant identifier
 * @param {object} updates - { name?, logo?, icon?, theme?, features? }
 */
export function updateAdminTenant(tenantId, updates) {
  return apiClient
    .put(`/api/v1/admin/tenants/${tenantId}`, updates)
    .then((r) => r.data);
}

/**
 * Admin: delete a tenant.
 *
 * @param {string} tenantId - Tenant identifier
 */
export function deleteAdminTenant(tenantId) {
  return apiClient
    .delete(`/api/v1/admin/tenants/${tenantId}`)
    .then((r) => r.data);
}

export function updateAgencyMembership(tenantId, agencyId, userId, updates) {
  return apiClient
    .patch(
      `/api/v1/agencies/${agencyId}/memberships/${userId}`,
      updates,
      withTenant(tenantId),
    )
    .then((r) => r.data);
}

export function fetchAgencyRoleTypes(tenantId, agencyId) {
  return apiClient
    .get(`/api/v1/agencies/${agencyId}/role-types`, withTenant(tenantId))
    .then((r) => r.data);
}

export function createAgencyRoleType(tenantId, agencyId, roleType) {
  return apiClient
    .post(`/api/v1/agencies/${agencyId}/role-types`, roleType, withTenant(tenantId))
    .then((r) => r.data);
}

export function updateAgencyRoleType(tenantId, agencyId, id, updates) {
  return apiClient
    .put(`/api/v1/agencies/${agencyId}/role-types/${id}`, updates, withTenant(tenantId))
    .then((r) => r.data);
}

export function deleteAgencyRoleType(tenantId, agencyId, id) {
  return apiClient
    .delete(`/api/v1/agencies/${agencyId}/role-types/${id}`, withTenant(tenantId))
    .then((r) => r.data);
}

export function sendAgencyInvite(tenantId, agencyId, email) {
  return apiClient
    .post(
      `/api/v1/agencies/${agencyId}/invites`,
      { email },
      withTenant(tenantId),
    )
    .then((r) => r.data);
}

// ── Dashboard endpoints (no tenant in path, optional via header) ─────────────

export function fetchTranscript(userId, params) {
  return apiClient
    .get(`/api/v1/users/${userId}/transcript`, { params })
    .then((r) => r.data);
}

export function fetchTeachingTranscript(userId, params) {
  return apiClient
    .get(`/api/v1/users/${userId}/teaching-transcript`, { params })
    .then((r) => r.data);
}

export function fetchDashboard(userId) {
  return apiClient.get(`/api/v1/users/${userId}/dashboard`).then((r) => r.data);
}

export function fetchDashboardLayout(role, tenantId) {
  return apiClient
    .get(`/api/v1/dashboard-layouts/${role}`, {
      ...withTenant(tenantId),
      validateStatus: (status) => status === 200 || status === 204,
    })
    .then((response) => (response.status === 204 ? null : response.data));
}

export function saveDashboardLayout(role, layout, tenantId) {
  return apiClient
    .put(`/api/v1/dashboard-layouts/${role}`, layout, withTenant(tenantId))
    .then((response) => response.data);
}

export function resetDashboardLayout(role, tenantId) {
  return apiClient.delete(
    `/api/v1/dashboard-layouts/${role}`,
    withTenant(tenantId),
  );
}

// ── Training center endpoints (tenant required via header) ───────────────────

export function fetchTrainingCenterInfo(tenantId) {
  return apiClient
    .get(`/api/v1/training-center`, withTenant(tenantId))
    .then((r) => r.data);
}

export function updateTrainingCenterInfo(tenantId, info) {
  return apiClient
    .put(`/api/v1/training-center`, info, withTenant(tenantId))
    .then((r) => r.data);
}

// ── Multi-tenant role management endpoints ───────────────────────────────────

/** Get the current user's per-tenant roles and pending invitations. */
export function fetchMyTenantRoles() {
  return apiClient.get(`/api/v1/user-tenant-roles/my`).then((r) => r.data);
}

/** Send a role invitation to a user for a tenant. role must be a Role enum name. */
export function sendRoleInvitation(invitedUserId, tenantId, role) {
  return apiClient
    .post(`/api/v1/user-tenant-roles/invitations`, {
      invitedUserId,
      tenantId,
      role,
    })
    .then((r) => r.data);
}

/** Accept a role invitation by ID. */
export function acceptRoleInvitation(invitationId) {
  return apiClient
    .patch(`/api/v1/user-tenant-roles/invitations/${invitationId}/accept`)
    .then((r) => r.data);
}

/** Decline a role invitation by ID. */
export function declineRoleInvitation(invitationId) {
  return apiClient
    .patch(`/api/v1/user-tenant-roles/invitations/${invitationId}/decline`)
    .then((r) => r.data);
}

// ── Admin: user-tenant role management ───────────────────────────────────────

/** Admin: list all users with their per-tenant roles. Optional search query. */
export function fetchTenantUsersByRole(tenantId, role) {
  return apiClient
    .get(`/api/v1/user-tenant-roles/tenant/${tenantId}/users`, {
      params: { role },
    })
    .then((r) => r.data);
}

/** Coordinator: userId → granted teaching roles for every instructor in the tenant. */
export function fetchInstructorQualifications(tenantId) {
  return apiClient
    .get(`/api/v1/instructor-qualifications`, { headers: { "X-Tenant-Id": tenantId } })
    .then((r) => r.data);
}

/** Coordinator: replace an instructor's granted teaching roles (LEAD_INSTRUCTOR / ASSISTANT_INSTRUCTOR / TA). */
export function setInstructorQualifications(userId, teachingRoles, tenantId) {
  return apiClient
    .put(`/api/v1/instructors/${userId}/qualifications`, { teachingRoles }, { headers: { "X-Tenant-Id": tenantId } })
    .then((r) => r.data);
}

/** The caller's own granted teaching roles: { teachingRoles: [...] }. */
export function fetchMyTeachingRoles(userId, tenantId) {
  return apiClient
    .get(`/api/v1/instructors/${userId}/qualifications`, { headers: { "X-Tenant-Id": tenantId } })
    .then((r) => r.data);
}

export function removeTenantRole(userId, tenantId, role) {
  return apiClient
    .delete(
      `/api/v1/user-tenant-roles/tenant/${tenantId}/users/${userId}/roles/${role}`,
    )
    .then((r) => r.data);
}

export function adminFetchUsersWithRoles(q) {
  return apiClient
    .get(`/api/v1/admin/user-tenant-roles/users`, { params: q ? { q } : {} })
    .then((r) => r.data);
}

/** Admin: directly assign a role to a user in a tenant. */
export function adminAssignRole(userId, tenantId, role) {
  return apiClient
    .post(`/api/v1/admin/user-tenant-roles`, { userId, tenantId, role })
    .then((r) => r.data);
}

/** Admin: remove a specific role from a user in a tenant. */
export function adminRemoveRole(userId, tenantId, role) {
  return apiClient
    .delete(`/api/v1/admin/user-tenant-roles/${userId}/${tenantId}/${role}`)
    .then((r) => r.data);
}

/** Global Admin: grant or revoke another user's GLOBAL_ADMIN role. */
export function adminSetGlobalAdmin(userId, globalAdmin) {
  return apiClient
    .put(`/api/v1/admin/users/${userId}/global-admin`, { globalAdmin })
    .then((r) => r.data);
}

/** Admin: lock or unlock a user account. */
export function adminLockUser(userId, locked) {
  return apiClient
    .patch(`/api/v1/admin/users/${userId}/lock`, { locked })
    .then((r) => r.data);
}

/** Admin: permanently delete a user. */
export function adminDeleteUser(userId) {
  return apiClient.delete(`/api/v1/admin/users/${userId}`).then((r) => r.data);
}

/** Admin: check mapped import rows — per-row field errors and whether each email already has an account. */
export function validateUserImport(rows) {
  return apiClient
    .post(`/api/v1/admin/user-import/validate`, { rows })
    .then((r) => r.data);
}

/** Admin: create/attach the imported users in a tenant. 400 carries `rowErrors` when rows need fixing. */
export function importUsers(tenantId, rows, sendWelcomeEmail) {
  return apiClient
    .post(`/api/v1/admin/user-import`, { tenantId, rows, sendWelcomeEmail })
    .then((r) => r.data);
}

// ── Security / Auth ──────────────────────────────────────────────────────────

export function changePassword(userId, currentPassword, newPassword) {
  return apiClient
    .patch(`/api/v1/users/${userId}/password`, { currentPassword, newPassword })
    .then((r) => r.data);
}

// Passwordless sign-in: request a one-time emailed link, then redeem it. `next` is an optional QR check-in
// path (/checkin?h=...) to land on after signing in; the server drops anything else.
export function requestMagicLink(email, next) {
  return apiClient
    .post("/api/v1/auth/magic-link", next ? { email, next } : { email })
    .then((r) => r.data);
}

export function consumeMagicLink(token) {
  return apiClient
    .post("/api/v1/auth/magic-link/consume", { token })
    .then((r) => r.data);
}

// Email verification (forced for self-registered accounts).
export function verifyEmail(token) {
  return apiClient
    .post("/api/v1/auth/verify-email", { token })
    .then((r) => r.data);
}

export function resendVerification(email) {
  return apiClient
    .post("/api/v1/auth/resend-verification", { email })
    .then((r) => r.data);
}

export function forgotPassword(email) {
  return apiClient
    .post("/api/v1/auth/forgot-password", { email })
    .then((r) => r.data);
}

export function resetPassword(token, newPassword) {
  return apiClient
    .post("/api/v1/auth/reset-password", { token, newPassword })
    .then((r) => r.data);
}

export function verifyTwoFactor(preAuthToken, code) {
  return apiClient
    .post("/api/v1/auth/verify-2fa", { preAuthToken, code })
    .then((r) => r.data);
}

export function setupTotp() {
  return apiClient.get("/api/v1/auth/2fa/setup").then((r) => r.data);
}

export function enableTotp(code) {
  return apiClient
    .post("/api/v1/auth/2fa/enable", { code })
    .then((r) => r.data);
}

export function disableTotp(currentPassword) {
  return apiClient
    .post("/api/v1/auth/2fa/disable", { currentPassword })
    .then((r) => r.data);
}

export function listSessions(userId) {
  return apiClient.get(`/api/v1/users/${userId}/sessions`).then((r) => r.data);
}

export function revokeSession(userId, sessionId) {
  return apiClient
    .delete(`/api/v1/users/${userId}/sessions/${sessionId}`)
    .then((r) => r.data);
}

// The server keeps the caller's own session (taken from the httpOnly auth cookie).
export function revokeOtherSessions(userId) {
  return apiClient
    .delete(`/api/v1/users/${userId}/sessions`)
    .then((r) => r.data);
}

/** Admin: list all system-wide pending role invitations. */
export function adminFetchPendingInvitations() {
  return apiClient
    .get(`/api/v1/admin/user-tenant-roles/invitations`)
    .then((r) => r.data);
}

/** Fetch PENDING role invitations for a specific tenant. Pass role to filter (e.g. 'INSTRUCTOR'). */
export function fetchTenantRoleInvitations(tenantId, role) {
  return apiClient
    .get(`/api/v1/user-tenant-roles/invitations/by-tenant/${tenantId}`, {
      params: role ? { role } : {},
    })
    .then((r) => r.data);
}

/** Invite a user to an agency by email address. */
export function inviteAgencyMember(agencyId, email) {
  return apiClient
    .post(`/api/v1/agencies/${agencyId}/invites`, { email })
    .then((r) => r.data);
}

// ── Agency COI (Certificate of Insurance) endpoints ──────────────────────────

/** List all COI records for an agency. */
export function fetchAgencyCois(tenantId, agencyId) {
  return apiClient
    .get(`/api/v1/agencies/${agencyId}/coi`, withTenant(tenantId))
    .then((r) => r.data);
}

/**
 * Upload (create) a new COI record for an agency.
 * @param {object} data - { filename, documentUrl, validFrom, validTo, notes }
 */
export function uploadAgencyCoi(tenantId, agencyId, data) {
  return apiClient
    .post(`/api/v1/agencies/${agencyId}/coi`, data, withTenant(tenantId))
    .then((r) => r.data);
}

/** Delete a COI record. */
export function deleteAgencyCoi(tenantId, agencyId, coiId) {
  return apiClient
    .delete(`/api/v1/agencies/${agencyId}/coi/${coiId}`, withTenant(tenantId))
    .then((r) => r.data);
}

/**
 * Check whether a user has a valid COI on file for the current tenant.
 * Returns { satisfied: boolean }
 */
export function checkUserCoi(tenantId, userId) {
  return apiClient
    .get(`/api/v1/coi/check`, mergeConfig(tenantId, { params: { userId } }))
    .then((r) => r.data);
}

/**
 * Fetch the COI compliance report for a class (coordinators / admins only).
 * Returns an array of student + COI snapshot rows.
 */
export function fetchClassCoiReport(tenantId, classId) {
  return apiClient
    .get(`/api/v1/classes/${classId}/coi-report`, withTenant(tenantId))
    .then((r) => r.data);
}

export function sendInstructorEmailInvitation(email, tenantId) {
  return apiClient
    .post("/api/v1/instructor-invitations", { email, tenantId })
    .then((r) => r.data);
}

export function fetchInstructorEmailInvitations(tenantId) {
  return apiClient
    .get(`/api/v1/instructor-invitations/by-tenant/${tenantId}`)
    .then((r) => r.data);
}

export function fetchInstructorEmailInvitationHistory(tenantId) {
  return apiClient
    .get(`/api/v1/instructor-invitations/by-tenant/${tenantId}/history`)
    .then((r) => r.data);
}

export function revokeInstructorEmailInvitation(id) {
  return apiClient
    .delete(`/api/v1/instructor-invitations/${id}`)
    .then((r) => r.data);
}

export function resendInstructorEmailInvitation(id) {
  return apiClient
    .post(`/api/v1/instructor-invitations/${id}/resend`)
    .then((r) => r.data);
}

export function validateInstructorInviteToken(token) {
  return apiClient
    .get(`/api/public/instructor-invitations/${token}`)
    .then((r) => r.data);
}

export function acceptInstructorInvite(token, username, password, fullName) {
  return apiClient
    .post(`/api/public/instructor-invitations/${token}/accept`, {
      username,
      password,
      fullName,
    })
    .then((r) => r.data);
}

export function fetchStudentCourseProfile(courseId, userId) {
  return apiClient
    .get(`/api/v1/classes/${courseId}/students/${userId}/profile`)
    .then((r) => r.data);
}

/** overrideReason (coordinator/admin only): pass a student who has not met the class requirements. */
export function signOffStudent(courseId, userId, passed, overrideReason) {
  const body = overrideReason ? { passed, overrideReason } : { passed };
  return apiClient
    .post(`/api/v1/classes/${courseId}/students/${userId}/sign-off`, body)
    .then((r) => r.data);
}

// ── Session request endpoints ────────────────────────────────────────────────

export function submitSessionRequest(courseId, data) {
  return apiClient
    .post(`/api/v1/courses/${courseId}/session-requests`, data)
    .then((r) => r.data);
}

export function fetchPendingSessionRequests() {
  return apiClient
    .get(`/api/v1/session-requests/pending`)
    .then((r) => unwrapDataOrArray(r));
}

export function fetchMySessionRequests(userId) {
  return apiClient
    .get(`/api/v1/users/${userId}/session-requests`)
    .then((r) => unwrapDataOrArray(r));
}

export function fetchSessionRequestsForCourse(courseId) {
  return apiClient
    .get(`/api/v1/courses/${courseId}/session-requests`)
    .then((r) => unwrapDataOrArray(r));
}

export function approveSessionRequest(requestId) {
  return apiClient
    .post(`/api/v1/session-requests/${requestId}/approve`)
    .then((r) => r.data);
}

export function rejectSessionRequest(requestId, reason) {
  return apiClient
    .post(`/api/v1/session-requests/${requestId}/reject`, { reason })
    .then((r) => r.data);
}

export function cancelSessionRequest(requestId, instructorId) {
  return apiClient
    .delete(`/api/v1/session-requests/${requestId}`, { data: { instructorId } })
    .then((r) => r.data);
}

// ── TEMPLATE BANKS ────────────────────────────────────────────────────────────

// Skill Sheet Bank
export function fetchSkillSheetBank(q) {
  return apiClient
    .get("/api/v1/bank/skill-sheets", { params: q ? { q } : {} })
    .then((r) => r.data);
}
export function fetchSkillSheetBankItem(id) {
  return apiClient.get(`/api/v1/bank/skill-sheets/${id}`).then((r) => r.data);
}
export function createSkillSheetBankItem(data) {
  return apiClient.post("/api/v1/bank/skill-sheets", data).then((r) => r.data);
}
export function updateSkillSheetBankItem(id, data) {
  return apiClient
    .put(`/api/v1/bank/skill-sheets/${id}`, data)
    .then((r) => r.data);
}
export function deleteSkillSheetBankItem(id) {
  return apiClient
    .delete(`/api/v1/bank/skill-sheets/${id}`)
    .then((r) => r.data);
}
export function publishSkillSheetBankItem(id) {
  return apiClient
    .post(`/api/v1/bank/skill-sheets/${id}/publish`)
    .then((r) => r.data);
}
export function unpublishSkillSheetBankItem(id) {
  return apiClient.post(`/api/v1/bank/skill-sheets/${id}/unpublish`).then((r) => r.data);
}
export function duplicateSkillSheetBankItem(id) {
  return apiClient.post(`/api/v1/bank/skill-sheets/${id}/duplicate`).then((r) => r.data);
}
export function fetchSkillSheetBankAssignments(id) {
  return apiClient
    .get(`/api/v1/bank/skill-sheets/${id}/assignments`)
    .then((r) => r.data);
}
export function fetchSkillSheetBankVersions(id) {
  return apiClient
    .get(`/api/v1/bank/skill-sheets/${id}/versions`)
    .then((r) => r.data);
}
export function unassignSkillSheetFromCourse(sheetId, courseId) {
  return apiClient
    .delete(
      `/api/v1/bank/skill-sheets/${sheetId}/assignments/course/${courseId}`,
    )
    .then((r) => r.data);
}
export function fetchTestBankAssignments(id) {
  return apiClient
    .get(`/api/v1/bank/tests/${id}/assignments`)
    .then((r) => r.data);
}
export function fetchTestBankVersions(id) {
  return apiClient.get(`/api/v1/bank/tests/${id}/versions`).then((r) => r.data);
}
export function unassignTestFromCourse(testId, courseId) {
  return apiClient
    .delete(`/api/v1/bank/tests/${testId}/assignments/course/${courseId}`)
    .then((r) => r.data);
}
export function fetchDocumentBankAssignments(id) {
  return apiClient
    .get(`/api/v1/bank/documents/${id}/assignments`)
    .then((r) => r.data);
}
export function unassignDocumentFromCourse(docId, courseId) {
  return apiClient
    .delete(`/api/v1/bank/documents/${docId}/assignments/course/${courseId}`)
    .then((r) => r.data);
}

// Test Bank
export function fetchTestBank(q) {
  return apiClient
    .get("/api/v1/bank/tests", { params: q ? { q } : {} })
    .then((r) => r.data);
}
export function fetchTestBankItem(id) {
  return apiClient.get(`/api/v1/bank/tests/${id}`).then((r) => r.data);
}
export function createTestBankItem(data) {
  return apiClient.post("/api/v1/bank/tests", data).then((r) => r.data);
}
export function updateTestBankItem(id, data) {
  return apiClient.put(`/api/v1/bank/tests/${id}`, data).then((r) => r.data);
}
export function deleteTestBankItem(id) {
  return apiClient.delete(`/api/v1/bank/tests/${id}`).then((r) => r.data);
}
export function publishTestBankItem(id) {
  return apiClient.post(`/api/v1/bank/tests/${id}/publish`).then((r) => r.data);
}
export function unpublishTestBankItem(id) {
  return apiClient.post(`/api/v1/bank/tests/${id}/unpublish`).then((r) => r.data);
}
export function duplicateTestBankItem(id) {
  return apiClient.post(`/api/v1/bank/tests/${id}/duplicate`).then((r) => r.data);
}

// Knowledge-base import (Gemini-parsed test/skill-sheet SQL paste-back)
export function importKnowledgeSql(sql) {
  return apiClient
    .post("/api/v1/bank/knowledge-import", { sql })
    .then((r) => r.data);
}

// Gemini-backed skill sheet / test import (server-side extraction)
export function uploadGeminiImport(type, file, batchId) {
  const fd = new FormData();
  fd.append("file", file);
  const url = batchId
    ? `/api/v1/bank/gemini-import/${type}?batchId=${encodeURIComponent(batchId)}`
    : `/api/v1/bank/gemini-import/${type}`;
  return apiClient
    .post(url, fd, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
}

export function confirmGeminiImport(batchId, rowIds) {
  return apiClient
    .post(`/api/v1/bank/gemini-import/${batchId}/confirm`, { rowIds })
    .then((r) => r.data);
}

export function discardGeminiImport(batchId) {
  return apiClient.delete(`/api/v1/bank/gemini-import/${batchId}`).then((r) => r.data);
}

/** Applies review-step edits to a still-pending staged row before it's confirmed. */
export function updateGeminiImportRow(batchId, rowId, payload) {
  return apiClient
    .patch(`/api/v1/bank/gemini-import/${batchId}/rows/${rowId}`, payload)
    .then((r) => r.data);
}

/** Polls a row's real extraction progress (status/phase) while the backend's 3-pass pipeline runs. */
export function getGeminiImportRowStatus(batchId, rowId) {
  return apiClient
    .get(`/api/v1/bank/gemini-import/${batchId}/rows/${rowId}`)
    .then((r) => r.data);
}

/** Every live row in a batch - used to find the extra skill sheets split out of a multi-sheet upload. */
export function listGeminiImportRows(batchId) {
  return apiClient
    .get(`/api/v1/bank/gemini-import/${batchId}/rows`)
    .then((r) => r.data);
}

// Document Bank
export function fetchDocumentBank(q) {
  return apiClient
    .get("/api/v1/bank/documents", { params: q ? { q } : {} })
    .then((r) => r.data);
}
export function fetchDocumentBankItem(id) {
  return apiClient.get(`/api/v1/bank/documents/${id}`).then((r) => r.data);
}
export function createDocumentBankItem(formData) {
  if (formData instanceof FormData) {
    return apiClient
      .post("/api/v1/bank/documents", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  }
  return apiClient.post("/api/v1/bank/documents", formData).then((r) => r.data);
}
export function updateDocumentBankItem(id, formData) {
  if (formData instanceof FormData) {
    return apiClient
      .put(`/api/v1/bank/documents/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  }
  return apiClient
    .put(`/api/v1/bank/documents/${id}`, formData)
    .then((r) => r.data);
}
export function deleteDocumentBankItem(id) {
  return apiClient.delete(`/api/v1/bank/documents/${id}`).then((r) => r.data);
}
export function publishDocumentBankItem(id) {
  return apiClient
    .post(`/api/v1/bank/documents/${id}/publish`)
    .then((r) => r.data);
}
export function unpublishDocumentBankItem(id) {
  return apiClient.post(`/api/v1/bank/documents/${id}/unpublish`).then((r) => r.data);
}
export function duplicateDocumentBankItem(id) {
  return apiClient.post(`/api/v1/bank/documents/${id}/duplicate`).then((r) => r.data);
}
export function downloadDocumentBankFile(id) {
  return apiClient
    .get(`/api/v1/bank/documents/${id}/download`, { responseType: "blob" })
    .then((r) => r.data);
}

// Marketplace
export function searchMarketplace({ type, q, category, page, size } = {}) {
  const params = {};
  if (type) params.type = type;
  if (q) params.q = q;
  if (category) params.category = category;
  if (page != null) params.page = page;
  if (size != null) params.size = size;
  return apiClient.get("/api/v1/marketplace/search", { params }).then((r) => r.data);
}
export function publishToMarketplace(type, id) {
  return apiClient.post(`/api/v1/marketplace/${type}/${id}/publish`).then((r) => r.data);
}
/** Takes the caller's listing for this item off the marketplace. */
export function withdrawFromMarketplace(type, id) {
  return apiClient.delete(`/api/v1/marketplace/${type}/${id}`).then((r) => r.data);
}
export function updateMarketplaceListing(type, id) {
  return apiClient.post(`/api/v1/marketplace/${type}/${id}/update-publish`).then((r) => r.data);
}
/** One listing as this tenant sees it (includes importedItemId / importedAtVersion when they have a copy). */
export function getMarketplaceListing(listingId) {
  return apiClient.get(`/api/v1/marketplace/listing/${listingId}`).then((r) => r.data);
}

/** mode: "overwrite" (default, updates the tenant's existing copy) or "copy" (always a separate new copy). */
export function importFromMarketplace(listingId, type, mode) {
  const params = mode ? { type, mode } : { type };
  return apiClient.post(`/api/v1/marketplace/listing/${listingId}/import`, null, { params }).then((r) => r.data);
}

// Course Bank Assignments
export function fetchCourseSkillSheetAssignments(courseId) {
  return apiClient
    .get(`/api/v1/classes/${courseId}/bank/skill-sheets`)
    .then((r) => r.data);
}
export function assignSkillSheetToCourse(courseId, data) {
  return apiClient
    .post(`/api/v1/classes/${courseId}/bank/skill-sheets`, data)
    .then((r) => r.data);
}
export function updateSkillSheetAssignment(courseId, assignId, data) {
  return apiClient
    .patch(`/api/v1/classes/${courseId}/bank/skill-sheets/${assignId}`, data)
    .then((r) => r.data);
}
export function removeSkillSheetAssignment(courseId, assignId) {
  return apiClient
    .delete(`/api/v1/classes/${courseId}/bank/skill-sheets/${assignId}`)
    .then((r) => r.data);
}
export function pushSkillSheetAssignmentToBank(courseId, assignId, mode) {
  return apiClient
    .post(`/api/v1/classes/${courseId}/bank/skill-sheets/${assignId}/push-to-bank`, { mode })
    .then((r) => r.data);
}

export function fetchCourseTestAssignments(courseId) {
  return apiClient
    .get(`/api/v1/classes/${courseId}/bank/tests`)
    .then((r) => r.data);
}
export function assignTestToCourse(courseId, data) {
  return apiClient
    .post(`/api/v1/classes/${courseId}/bank/tests`, data)
    .then((r) => r.data);
}
export function pushTestToBank(testId, mode) {
  return apiClient
    .post(`/api/v1/tests/${testId}/publish-to-bank`, { mode })
    .then((r) => r.data);
}
export function updateTestAssignment(courseId, assignId, data) {
  return apiClient
    .patch(`/api/v1/classes/${courseId}/bank/tests/${assignId}`, data)
    .then((r) => r.data);
}
export function removeTestAssignment(courseId, assignId) {
  return apiClient
    .delete(`/api/v1/classes/${courseId}/bank/tests/${assignId}`)
    .then((r) => r.data);
}

export function fetchCourseDocumentAssignments(courseId) {
  return apiClient
    .get(`/api/v1/classes/${courseId}/bank/documents`)
    .then((r) => r.data);
}
export function assignDocumentToCourse(courseId, data) {
  return apiClient
    .post(`/api/v1/classes/${courseId}/bank/documents`, data)
    .then((r) => r.data);
}
export function updateDocumentAssignment(courseId, assignId, data) {
  return apiClient
    .patch(`/api/v1/classes/${courseId}/bank/documents/${assignId}`, data)
    .then((r) => r.data);
}
export function removeDocumentAssignment(courseId, assignId) {
  return apiClient
    .delete(`/api/v1/classes/${courseId}/bank/documents/${assignId}`)
    .then((r) => r.data);
}
export function pushDocumentAssignmentToBank(courseId, assignId, mode) {
  return apiClient
    .post(`/api/v1/classes/${courseId}/bank/documents/${assignId}/push-to-bank`, { mode })
    .then((r) => r.data);
}

// ── Messaging ─────────────────────────────────────────────────────────────────

export function fetchMessagingThreads() {
  return apiClient.get("/api/v1/messaging/threads").then((r) => r.data);
}

export function fetchMessagingThread(threadId) {
  return apiClient
    .get(`/api/v1/messaging/threads/${threadId}`)
    .then((r) => r.data);
}

export function createBroadcastThread({
  kind = "broadcast",
  classId,
  classTitle,
  studentCount,
  recipientId,
}) {
  return apiClient
    .post("/api/v1/messaging/threads", {
      kind,
      ...(kind === "broadcast"
        ? { classId, classTitle, studentCount }
        : { recipientId }),
    })
    .then((r) => r.data);
}

export function createDmThread(classId, otherUserId) {
  return apiClient
    .post("/api/v1/messaging/threads/dm", { classId, otherUserId })
    .then((r) => r.data);
}

export function createStaffDmThread(otherUserId) {
  return apiClient
    .post("/api/v1/messaging/threads/dm/staff", { otherUserId })
    .then((r) => r.data);
}

export function createGroupThread({ participantIds, title }) {
  return apiClient
    .post("/api/v1/messaging/threads", { kind: "group", participantIds, title })
    .then((r) => r.data);
}

export function markThreadRead(threadId) {
  return apiClient
    .put(`/api/v1/messaging/threads/${threadId}/read`)
    .then((r) => r.data);
}

export function sendMessagingMessage(
  threadId,
  content,
  attachmentIds = [],
  options = {},
) {
  return apiClient
    .post(`/api/v1/messaging/threads/${threadId}/messages`, {
      content,
      ...(attachmentIds.length > 0 && { attachmentIds }),
      ...(options.pinToDashboard && { pinToDashboard: true }),
      ...(options.type && { type: options.type }),
    })
    .then((r) => r.data);
}

export function fetchThreadContext(threadId) {
  return apiClient
    .get(`/api/v1/messaging/threads/${threadId}/context`)
    .then((r) => r.data);
}

export function unpinThreadMessage(threadId) {
  return apiClient
    .post(`/api/v1/messaging/threads/${threadId}/unpin`)
    .then((r) => r.data);
}

export function uploadMessageAttachment(threadId, file, onProgress) {
  const formData = new FormData();
  formData.append("file", file);
  return apiClient
    .post(`/api/v1/messaging/threads/${threadId}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress
        ? (e) => onProgress(e.loaded, e.total)
        : undefined,
    })
    .then((r) => r.data);
}

export function fetchAttachmentDownloadUrl(attachmentId) {
  return apiClient
    .get(`/api/v1/messaging/attachments/${attachmentId}/download-url`)
    .then((r) => r.data.url);
}

export function fetchMessagingDirectory() {
  return apiClient.get("/api/v1/messaging/directory").then((r) => r.data);
}

export function fetchClassAnnouncementPreview(classId) {
  return apiClient
    .get(`/api/v1/messaging/threads/broadcast/by-class/${classId}`)
    .then((r) => r.data);
}

const COURSE_REPORT_DOWNLOADS = {
  attendance: { path: "attendance.csv", filename: "attendance-summary.csv" },
  tests: { path: "tests.csv", filename: "test-results.csv" },
  skills: { path: "skills.csv", filename: "skill-checklists.csv" },
  completion: { path: "completion.csv", filename: "completion-readiness.csv" },
  incomplete: {
    path: "incomplete.csv",
    filename: "incomplete-requirements.csv",
  },
  coi_compliance: {
    path: "coi-compliance.csv",
    filename: "coi-compliance.csv",
  },
  full_export: { path: "full.zip", filename: "class-report-export.zip" },
};

function filenameFromContentDisposition(header, fallback) {
  if (!header) return fallback;
  const star = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (star?.[1]) return decodeURIComponent(star[1].replace(/"/g, ""));
  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const bare = header.match(/filename=([^;]+)/i);
  return bare?.[1]?.trim() || fallback;
}

export function downloadCourseReport(courseId, reportId) {
  const report = COURSE_REPORT_DOWNLOADS[reportId];
  if (!report) {
    return Promise.reject(new Error(`Unknown course report: ${reportId}`));
  }
  return apiClient
    .get(`/api/v1/courses/${courseId}/reports/${report.path}`, {
      responseType: "blob",
    })
    .then((response) => {
      const filename = filenameFromContentDisposition(
        response.headers?.["content-disposition"],
        report.filename,
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      return { filename };
    });
}
